import * as vp from 'vopay';
import { JSX, ReactNode, createContext, useContext, useEffect, useState } from 'react';
import moment from 'moment';
import useFetchUserRoles from '../hooks/useApi/useFetchUserRoles';
import useFetchApprovalTransactionsInbox from '../hooks/useApi/useFetchApprovalTransactionsInbox';
import useFetchApprovalTransactions from '../hooks/useApi/useFetchApprovalTransactions';
import useFetchWorkflows from '../hooks/useApi/useFetchWorkflows';
import useFetchTransactionTypes from '../hooks/useApi/useFetchTransactionTypes';
import { useClientData } from './ClientData.provider';

type ApprovalWorkflow = {
  approvalTransactions: vp.ApprovalInboxTransactionsResponse;
  hasTransactionsRequiringApproval: boolean;
  roles: string[];
  isApprover: boolean;
  hasInvalidApprover: boolean;
  hasInvalidWorkflows: boolean;
  usersReset: () => void;
  transactionsReset: () => void;
  approvalListReset: () => void;
  areEnoughApprovers: (
    minimumAmount?: number,
    transactionTypes?: string[],
    requiredApprovers?: number,
    userRoles?: vp.UserRolesResponse,
  ) => boolean;
  getPaymentRail: (workflow: number, transactionTypes?: vp.TransactionTypesResponse) => string[];
};

export const ApprovalWorkflowContext = createContext<ApprovalWorkflow>({
  approvalTransactions: {
    Success: false,
    ErrorMessage: '',
    Transactions: {},
  },
  hasTransactionsRequiringApproval: false,
  roles: [],
  isApprover: false,
  hasInvalidApprover: false,
  hasInvalidWorkflows: false,
  usersReset: () => {},
  transactionsReset: () => {},
  approvalListReset: () => {},
  areEnoughApprovers: () => false,
  getPaymentRail: () => [],
});

export const ApprovalWorkflowProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const { response: userRolesResponse, fetchUserRoles: getUserRoles } = useFetchUserRoles();
  const [approvalTransactionsList, getApprovalTransactions] = useFetchApprovalTransactionsInbox({});
  const [allApprovalTransactionsList, getAllApprovalTransactions] = useFetchApprovalTransactions({});
  const [transactionTypesResponse, fetchTransactionTypes] = useFetchTransactionTypes({});
  const [workFlowsList, getWorkflows] = useFetchWorkflows({});
  const { response } = useClientData();
  const [approvalTransactions, setApprovalTransactions] = useState<vp.ApprovalInboxTransactionsResponse>({
    Success: false,
    ErrorMessage: '',
    Transactions: {},
  });
  const [isGetUserRoles, setGetUserRoles] = useState(false);
  const [isGetAprrovalTrasactions, setGetAprrovalTrasactions] = useState(false);
  const [isGetAllAprrovalTrasactions, setAllGetAprrovalTrasactions] = useState(false);
  const [isGetWorkflows, setGetWorkflows] = useState(false);
  const [isApprover, setIsApprover] = useState(false);
  const [roles, setRoles] = useState<string[]>([]);
  const [hasTransactionsRequiringApproval, setHasTransactionsRequiringApproval] = useState(false);
  const [hasInvalidWorkflows, setHasInvalidWorkflows] = useState(false);
  const [hasInvalidApprover, setHasInvalidApprover] = useState(false);
  const usersReset = () => {
    setGetUserRoles(true);
  };
  const transactionsReset = () => {
    setGetAprrovalTrasactions(true);
  };
  const timeDifference = moment().subtract(1, 'year').utc().format('YYYY-MM-DD');
  const currentDateTime = moment().utc().format('YYYY-MM-DD');
  const transactionsPeriod = {
    FromDate: timeDifference,
    ToDate: currentDateTime,
  };
  const approvalListReset = () => {
    setAllGetAprrovalTrasactions(true);
    setGetWorkflows(true);
  };
  useEffect(() => {
    if (response.Success) {
      setGetWorkflows(true);
      setAllGetAprrovalTrasactions(true);
      setGetAprrovalTrasactions(true);
      setGetUserRoles(true);
    }
  }, []);
  useEffect(() => {
    if (isGetAllAprrovalTrasactions) {
      getAllApprovalTransactions({
        StartDateTime: moment(transactionsPeriod.FromDate).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        EndDateTime: moment(transactionsPeriod.ToDate).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      });
      setAllGetAprrovalTrasactions(false);
    }
  }, [isGetAllAprrovalTrasactions]);
  useEffect(() => {
    if (isGetAprrovalTrasactions) {
      getApprovalTransactions({});
      setGetAprrovalTrasactions(false);
    }
  }, [isGetAprrovalTrasactions]);

  useEffect(() => {
    if (isGetUserRoles) {
      getUserRoles({
        Role: 'approver',
      });
      setGetUserRoles(false);
    }
  }, [isGetUserRoles]);
  useEffect(() => {
    if (isGetWorkflows) {
      getWorkflows({});
      fetchTransactionTypes({
        IsWithdraw: 1,
        CountriesID: response.Country === 'CA' ? 1 : 2,
        IsTransactionApprovalSupported: 1,
      });
      setGetWorkflows(false);
    }
  }, [isGetWorkflows]);

  useEffect(() => {
    if (userRolesResponse && userRolesResponse.Success) {
      setRoles(userRolesResponse.Roles);

      setIsApprover(Object.values(userRolesResponse.Roles).includes('approver'));
    }
  }, [userRolesResponse]);
  useEffect(() => {
    if (approvalTransactionsList && approvalTransactionsList.Success) {
      setApprovalTransactions(approvalTransactionsList);

      setHasTransactionsRequiringApproval(
        Object.values(approvalTransactionsList.Transactions).some((transaction) => !transaction.Approved),
      );
    }
  }, [approvalTransactionsList]);
  useEffect(() => {
    if (allApprovalTransactionsList && allApprovalTransactionsList.Success) {
      setHasInvalidApprover(
        Object.values(allApprovalTransactionsList.Transactions).some(
          (transaction) => transaction.NumberApprovalsRequired > transaction.AvailableApprovers,
        ),
      );
    }
  }, [allApprovalTransactionsList]);
  useEffect(() => {
    if (workFlowsList && workFlowsList.Success) {
      const { Workflows } = workFlowsList;

      const requiredApprovers = Object.values(Workflows).map((workflow) => {
        const { MinAmount, VIOAccountTransactionTypes_ID, NumberApprovalsRequired } = workflow;
        return areEnoughApprovers(
          MinAmount,
          getPaymentRail(VIOAccountTransactionTypes_ID, transactionTypesResponse),
          NumberApprovalsRequired,
          userRolesResponse,
        );
      });
      setHasInvalidWorkflows(requiredApprovers.includes(false));
    }
  }, [workFlowsList, transactionTypesResponse]);

  return (
    <ApprovalWorkflowContext.Provider
      value={{
        approvalTransactions,
        roles,
        hasTransactionsRequiringApproval,
        isApprover,
        hasInvalidApprover,
        hasInvalidWorkflows,
        usersReset,
        approvalListReset,
        transactionsReset,
        areEnoughApprovers,
        getPaymentRail,
      }}
    >
      {children}
    </ApprovalWorkflowContext.Provider>
  );

  function areEnoughApprovers(
    minimumAmountProp?: number | string,
    transactionTypes?: string[],
    requiredApprovers?: number,
    userRoles?: vp.UserRolesResponse,
  ) {
    if (!userRoles?.UserRolePermissions) return true;

    if (minimumAmountProp === undefined) return true;

    if (!requiredApprovers) return true;

    if (!transactionTypes) return true;

    if (transactionTypes.length === 0) return true;

    const roles = Object.values(userRoles.UserRolePermissions);
    const minimumAmount = Number(minimumAmountProp);

    for (const transactionType of transactionTypes) {
      const rolesForThisType = roles
        .filter((userRole) => {
          return userRole.TransactionType === transactionType;
        })
        .filter((userRole) => {
          return (
            userRole.MaxAmount === undefined ||
            userRole.MaxAmount === null ||
            Number(userRole.MaxAmount) >= minimumAmount
          );
        });

      if (rolesForThisType.length === 0) return false;

      if (rolesForThisType.length < Number(requiredApprovers)) return false;
    }

    return true;
  }
  function getPaymentRail(workflow: number, transactionTypes: vp.TransactionTypesResponse | undefined): string[] {
    const transactionType = Object.values(transactionTypes?.TransactionTypes || {}).find(
      (transactionTypesItem) => transactionTypesItem.ID === workflow,
    );

    return transactionType?.TransactionType.split('|') || [];
  }
};

export const useApprovalWorkflow = (): ApprovalWorkflow => useContext(ApprovalWorkflowContext);
